import { log, supabaseUrl, supabaseAnonKey, client } from ".";

const getAuthHeaders = async () => {
  const { data: session } = await client.auth.getSession();
  const headers = {
    apikey: supabaseAnonKey,
    "Content-Type": "application/json",
  };
  if (session?.session?.access_token) {
    headers.Authorization = `Bearer ${session.session.access_token}`;
  }
  return headers;
};

/**
 * Obtiene actividades CRM de un cliente basándose en los contactos asignados
 * @param {number} clienteId - ID del cliente
 * @param {number} [limit=10] - Cantidad máxima de actividades
 * @param {number|number[]} [contactosAgencia=null] - usuario_id(s) del contacto de agencia
 * @param {number|number[]} [contactosCliente=null] - usuario_id(s) del contacto del cliente
 * @returns {Promise<Array>} Lista de actividades con destinatarios
 */
export const getCrmActividadesByCliente = async (
  clienteId,
  limit = 10,
  contactosAgencia = null,
  contactosCliente = null
) => {
  try {
    const actividadIds = new Set();
    const headers = await getAuthHeaders();

    // Normalizar a arrays
    const agenciaIds = Array.isArray(contactosAgencia)
      ? contactosAgencia.filter(Boolean)
      : contactosAgencia
        ? [contactosAgencia]
        : [];

    const clienteIds = Array.isArray(contactosCliente)
      ? contactosCliente.filter(Boolean)
      : contactosCliente
        ? [contactosCliente]
        : [];

    // Obtener emails de los usuarios
    const allUserIds = [...agenciaIds, ...clienteIds].filter(Boolean);
    const allEmails = [];

    if (allUserIds.length > 0) {
      const { data: users } = await client.from("usuarios").select("id,email").in("id", allUserIds);
      if (users) {
        users.forEach((u) => {
          if (u.email) allEmails.push(u.email.toLowerCase());
        });
      }
    }

    // 1. Buscar actividades directamente vinculadas al cliente
    const directResponse = await fetch(`${supabaseUrl}/rest/v1/crm_actividades?cliente_id=eq.${clienteId}&select=id`, {
      method: "GET",
      headers,
    });
    if (directResponse.ok) {
      const data = await directResponse.json();
      data.forEach((a) => actividadIds.add(a.id));
    }

    // 2. Buscar en destinatarios por usuario_id (query combinada)
    if (allUserIds.length > 0) {
      const userIdResponse = await fetch(
        `${supabaseUrl}/rest/v1/crm_actividades_destinatarios?usuario_id=in.(${allUserIds.join(",")})&select=actividad_id`,
        { method: "GET", headers }
      );
      if (userIdResponse.ok) {
        const data = await userIdResponse.json();
        data.forEach((d) => actividadIds.add(d.actividad_id));
      }
    }

    // 3. Buscar en destinatarios por email (match exacto, case-insensitive)
    if (allEmails.length > 0) {
      const emailFilters = allEmails.map((e) => `email.ilike.${e}`).join(",");
      const emailResponse = await fetch(
        `${supabaseUrl}/rest/v1/crm_actividades_destinatarios?or=(${emailFilters})&select=actividad_id`,
        { method: "GET", headers }
      );
      if (emailResponse.ok) {
        const data = await emailResponse.json();
        data.forEach((d) => actividadIds.add(d.actividad_id));
      }
    }

    // 4. Obtener actividades completas y destinatarios
    let actividades = [];
    const ids = [...actividadIds];

    if (ids.length > 0) {
      const actividadesResponse = await fetch(
        `${supabaseUrl}/rest/v1/crm_actividades?id=in.(${ids.join(",")})&or=(cliente_id.is.null,cliente_id.eq.${clienteId})&order=fecha.desc&limit=${limit}`,
        { method: "GET", headers }
      );

      if (actividadesResponse.ok) {
        actividades = (await actividadesResponse.json()).filter((a) => !a.metadata?.masivo);
      }

      // Obtener todos los destinatarios en una sola query
      if (actividades.length > 0) {
        const actIds = actividades.map((a) => a.id).join(",");
        const destResponse = await fetch(
          `${supabaseUrl}/rest/v1/crm_actividades_destinatarios?actividad_id=in.(${actIds})`,
          { method: "GET", headers }
        );
        if (destResponse.ok) {
          const allDest = await destResponse.json();
          // Agrupar destinatarios por actividad_id
          actividades.forEach((actividad) => {
            actividad.destinatarios = allDest.filter((d) => d.actividad_id === actividad.id);
          });
        }
      }
    }

    // 5. Obtener ventas cerradas (sales + sales_contratos)
    const [salesResponse, contratosResponse] = await Promise.all([
      fetch(
        `${supabaseUrl}/rest/v1/sales?client_id=eq.${clienteId}&estatus=eq.100%25&fecha_cierre=not.is.null&eliminada=eq.false&select=id,fecha_cierre,importe,campana,code`,
        { method: "GET", headers }
      ),
      fetch(
        `${supabaseUrl}/rest/v1/sales_contratos?client_id=eq.${clienteId}&estatus=eq.100%25&fecha_cierre=not.is.null&eliminada=eq.false&select=id,fecha_cierre,importe_total,campana,display_id`,
        { method: "GET", headers }
      ),
    ]);

    if (salesResponse.ok) {
      const sales = await salesResponse.json();
      const saleActivities = sales.map((sale) => ({
        id: `sale-${sale.id}`,
        tipo: "SALE",
        fecha: sale.fecha_cierre,
        cliente_id: clienteId,
        destinatarios: [],
        metadata: {
          sale_id: sale.id,
          importe: sale.importe,
          campana: sale.campana,
          code: sale.code,
        },
        virtual: true,
      }));
      actividades.push(...saleActivities);
    }

    if (contratosResponse.ok) {
      const contratos = await contratosResponse.json();
      const contratoActivities = contratos.map((c) => ({
        id: `sale-contrato-${c.id}`,
        tipo: "SALE",
        fecha: c.fecha_cierre,
        cliente_id: clienteId,
        destinatarios: [],
        metadata: {
          sale_id: c.id,
          importe: c.importe_total,
          campana: c.campana,
          code: c.display_id,
          esContrato: true,
        },
        virtual: true,
      }));
      actividades.push(...contratoActivities);
    }

    // Ordenar por fecha descendente
    actividades.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));

    return actividades.slice(0, limit);
  } catch (error) {
    log.error("getCrmActividadesByCliente", error);
    throw error;
  }
};

/**
 * Obtiene todas las actividades CRM por país
 * @param {string} pais - País
 * @returns {Promise<Array>} Lista de actividades
 */
export const getCrmActividadesByPais = async (pais) => {
  try {
    const url = `${supabaseUrl}/rest/v1/crm_actividades?pais=eq.${pais}&order=fecha.desc`;
    const headers = await getAuthHeaders();
    const response = await fetch(url, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error("Error fetching CRM actividades by pais");
    }

    return await response.json();
  } catch (error) {
    log.error("getCrmActividadesByPais", error);
    throw error;
  }
};

/**
 * Verifica si existe una actividad SALE para una oportunidad
 * @param {number} oportunidadId - ID de la oportunidad
 * @returns {Promise<boolean>} true si existe
 */
export const existsSaleActivity = async (oportunidadId) => {
  try {
    const url = `${supabaseUrl}/rest/v1/crm_actividades?referencia_id=eq.${oportunidadId}&referencia_tipo=eq.oportunidad&tipo=eq.SALE`;
    const headers = await getAuthHeaders();
    const response = await fetch(url, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    return data.length > 0;
  } catch (error) {
    log.error("existsSaleActivity", error);
    return false;
  }
};

// ---------------------------------------------------------------------------
// Bulk loading: reemplaza N llamadas individuales por pocas queries globales
// ---------------------------------------------------------------------------

const PAGE_SIZE = 1000;
const ID_BATCH_SIZE = 500;
const EMAIL_BATCH_SIZE = 80;

const fetchPaginated = async (url, headers) => {
  const allData = [];
  let offset = 0;
  for (;;) {
    const sep = url.includes("?") ? "&" : "?";
    const pagedUrl = `${url}${sep}offset=${offset}&limit=${PAGE_SIZE}`;
    const response = await fetch(pagedUrl, { method: "GET", headers });
    if (!response.ok) break;
    const data = await response.json();
    allData.push(...data);
    if (data.length < PAGE_SIZE) break;
    offset += PAGE_SIZE;
  }
  return allData;
};

const fetchInIdBatches = async (baseUrl, paramName, ids, extraParams = "", headers) => {
  const allData = [];
  for (let i = 0; i < ids.length; i += ID_BATCH_SIZE) {
    const batch = ids.slice(i, i + ID_BATCH_SIZE);
    const url = `${baseUrl}?${paramName}=in.(${batch.join(",")})${extraParams}`;
    const data = await fetchPaginated(url, headers);
    allData.push(...data);
  }
  return allData;
};

/**
 * Construye mapa de contactos por cliente a partir de datos ya cargados.
 * Usa tabla relacional clientes_contactos_agencia si está disponible,
 * con fallback a campos legacy (contacto_agencia, contacto_agencia_1, etc.)
 */
export const buildContactMap = (clientes, usuarios, contactosAgencia = []) => {
  const emailByUserId = {};
  usuarios.forEach((u) => {
    if (u.email) emailByUserId[u.id] = u.email.toLowerCase();
  });

  // Indexar contactos relacionales por cliente_id
  const ctosRelMap = {};
  contactosAgencia.forEach((ca) => {
    if (!ctosRelMap[ca.cliente_id]) ctosRelMap[ca.cliente_id] = [];
    ctosRelMap[ca.cliente_id].push(ca.usuario_id);
  });
  const usaRelacional = contactosAgencia.length > 0;

  const contactMap = {};
  clientes.forEach((cliente) => {
    // Contactos de agencia: preferir relacional, fallback a legacy
    const agenciaIds =
      usaRelacional && ctosRelMap[cliente.id]
        ? ctosRelMap[cliente.id]
        : [
            cliente.contacto_agencia,
            cliente.contacto_agencia_1,
            cliente.contacto_agencia_2,
            cliente.contacto_agencia_3,
          ].filter(Boolean);

    // Contactos de cliente: siempre de campos legacy
    const clienteIds = [
      cliente.contacto_cliente,
      cliente.contacto_cliente_1,
      cliente.contacto_cliente_2,
      cliente.contacto_cliente_3,
    ].filter(Boolean);

    const userIds = [...new Set([...agenciaIds, ...clienteIds])];
    const emails = userIds.map((uid) => emailByUserId[uid]).filter(Boolean);
    contactMap[cliente.id] = { userIds, emails };
  });

  return contactMap;
};

/**
 * Carga actividades CRM en bulk para un conjunto de clientes.
 * Reemplaza N llamadas individuales getCrmActividadesByCliente por ~5 queries paginadas.
 *
 * @param {string} paisCode - Código de país (CHI, PER)
 * @param {number[]} clienteIds - IDs de clientes a cargar
 * @param {Object} contactMap - { [clienteId]: { userIds: number[], emails: string[] } }
 * @param {number} [limitPerClient=50] - Máximo de actividades por cliente
 * @returns {Promise<Object>} { [clienteId]: actividades[] }
 */
export const fetchAllActividadesBulk = async (paisCode, clienteIds, contactMap, limitPerClient = 50) => {
  if (clienteIds.length === 0) return {};

  const headers = await getAuthHeaders();
  const baseUrl = `${supabaseUrl}/rest/v1`;

  // Mapa: clienteId -> Set<actividadId>
  const actividadIdsPorCliente = {};
  clienteIds.forEach((id) => {
    actividadIdsPorCliente[id] = new Set();
  });

  // Preparar mapas inversos para pasos 2 y 3
  const allUserIds = [...new Set(clienteIds.flatMap((id) => contactMap[id]?.userIds || []))];
  const userIdToClientes = {};
  clienteIds.forEach((cId) => {
    (contactMap[cId]?.userIds || []).forEach((uid) => {
      if (!userIdToClientes[uid]) userIdToClientes[uid] = [];
      userIdToClientes[uid].push(cId);
    });
  });

  const allEmails = [...new Set(clienteIds.flatMap((id) => contactMap[id]?.emails || []))];
  const emailToClientes = {};
  clienteIds.forEach((cId) => {
    (contactMap[cId]?.emails || []).forEach((email) => {
      if (!emailToClientes[email]) emailToClientes[email] = [];
      emailToClientes[email].push(cId);
    });
  });

  // Pasos 1, 2, 3 en paralelo (son independientes)
  const fetchDirectas = fetchInIdBatches(
    `${baseUrl}/crm_actividades`,
    "cliente_id",
    clienteIds,
    `&pais=eq.${paisCode}&select=id,cliente_id`,
    headers
  );

  let fetchByUserId = Promise.resolve([]);
  if (allUserIds.length > 0) {
    fetchByUserId = fetchInIdBatches(
      `${baseUrl}/crm_actividades_destinatarios`,
      "usuario_id",
      allUserIds,
      "&select=actividad_id,usuario_id",
      headers
    );
  }

  const fetchByEmail = async () => {
    if (allEmails.length === 0) return [];
    const results = [];
    for (let i = 0; i < allEmails.length; i += EMAIL_BATCH_SIZE) {
      const batch = allEmails.slice(i, i + EMAIL_BATCH_SIZE);
      const emailFilters = batch.map((e) => `email.eq.${e}`).join(",");
      const url = `${baseUrl}/crm_actividades_destinatarios?or=(${emailFilters})&select=actividad_id,email`;
      const data = await fetchPaginated(url, headers);
      results.push(...data);
    }
    return results;
  };

  const [directas, destByUser, destByEmail] = await Promise.all([fetchDirectas, fetchByUserId, fetchByEmail()]);

  // Distribuir IDs de actividades por cliente
  directas.forEach((a) => {
    if (actividadIdsPorCliente[a.cliente_id]) {
      actividadIdsPorCliente[a.cliente_id].add(a.id);
    }
  });
  destByUser.forEach((d) => {
    const clientes = userIdToClientes[d.usuario_id] || [];
    clientes.forEach((cId) => actividadIdsPorCliente[cId].add(d.actividad_id));
  });
  destByEmail.forEach((d) => {
    const emailLower = (d.email || "").toLowerCase();
    const clientes = emailToClientes[emailLower] || [];
    clientes.forEach((cId) => actividadIdsPorCliente[cId].add(d.actividad_id));
  });

  // Pasos 4a + 4b + 5 en paralelo (todos usan todosLosIds, sin dependencias entre sí)
  const todosLosIds = [...new Set(clienteIds.flatMap((cId) => [...actividadIdsPorCliente[cId]]))];

  let fetchActividades = Promise.resolve([]);
  let fetchDest = Promise.resolve([]);
  if (todosLosIds.length > 0) {
    fetchActividades = fetchInIdBatches(
      `${baseUrl}/crm_actividades`,
      "id",
      todosLosIds,
      `&or=(cliente_id.is.null,cliente_id.in.(${clienteIds.join(",")}))&order=fecha.desc&select=*`,
      headers
    );
    fetchDest = fetchInIdBatches(`${baseUrl}/crm_actividades_destinatarios`, "actividad_id", todosLosIds, "", headers);
  }

  const fetchSales = fetchInIdBatches(
    `${baseUrl}/sales`,
    "client_id",
    clienteIds,
    "&estatus=eq.100%25&fecha_cierre=not.is.null&eliminada=eq.false" +
      "&select=id,client_id,fecha_cierre,importe,campana,code",
    headers
  );

  const fetchContratos = fetchInIdBatches(
    `${baseUrl}/sales_contratos`,
    "client_id",
    clienteIds,
    "&estatus=eq.100%25&fecha_cierre=not.is.null&eliminada=eq.false" +
      "&select=id,client_id,fecha_cierre,importe_total,campana,display_id",
    headers
  );

  const [actividadesRaw, destRaw, salesRaw, contratosRaw] = await Promise.all([
    fetchActividades,
    fetchDest,
    fetchSales,
    fetchContratos,
  ]);

  // Armar mapa de actividades y asignar destinatarios
  const actividadesById = {};
  actividadesRaw.forEach((a) => {
    if (a.metadata?.masivo) return; // Excluir mailings masivos (newsletters)
    actividadesById[a.id] = { ...a, destinatarios: [] };
  });
  destRaw.forEach((d) => {
    if (actividadesById[d.actividad_id]) {
      actividadesById[d.actividad_id].destinatarios.push(d);
    }
  });

  // Agrupar sales + contratos por client_id
  const salesPorCliente = {};
  salesRaw.forEach((sale) => {
    if (!salesPorCliente[sale.client_id]) salesPorCliente[sale.client_id] = [];
    salesPorCliente[sale.client_id].push({
      id: `sale-${sale.id}`,
      tipo: "SALE",
      fecha: sale.fecha_cierre,
      cliente_id: sale.client_id,
      destinatarios: [],
      metadata: {
        sale_id: sale.id,
        importe: sale.importe,
        campana: sale.campana,
        code: sale.code,
      },
      virtual: true,
    });
  });
  contratosRaw.forEach((c) => {
    if (!salesPorCliente[c.client_id]) salesPorCliente[c.client_id] = [];
    salesPorCliente[c.client_id].push({
      id: `sale-contrato-${c.id}`,
      tipo: "SALE",
      fecha: c.fecha_cierre,
      cliente_id: c.client_id,
      destinatarios: [],
      metadata: {
        sale_id: c.id,
        importe: c.importe_total,
        campana: c.campana,
        code: c.display_id,
        esContrato: true,
      },
      virtual: true,
    });
  });

  // 6. Distribuir actividades por cliente
  const resultado = {};
  clienteIds.forEach((cId) => {
    const actIds = actividadIdsPorCliente[cId];
    const actividades = [];

    actIds.forEach((actId) => {
      const actividad = actividadesById[actId];
      if (!actividad) return;
      // Si tiene cliente_id específico y no es este cliente, skip
      if (actividad.cliente_id && actividad.cliente_id !== cId) return;
      actividades.push(actividad);
    });

    // Agregar SALEs
    if (salesPorCliente[cId]) {
      actividades.push(...salesPorCliente[cId]);
    }

    // Sort por fecha desc + limit
    actividades.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
    resultado[cId] = actividades.slice(0, limitPerClient);
  });

  return resultado;
};

/**
 * Versión liviana de bulk fetch para MapaCalor.
 * Retorna solo la última fecha de actividad por email por cliente.
 * Encuentra actividades via email/userId en destinatarios (no via cliente_id).
 *
 * @param {string} paisCode - Código de país (CHI, PER)
 * @param {Array} clientes - Array de clientes con campos contacto_*
 * @param {Array} usuarios - Array de usuarios con id y email
 * @returns {Promise<Object>} { [clienteId]: { [email]: isoDateString } }
 */
export const fetchUltimasActividadesBulk = async (paisCode, clientes, usuarios, contactosAgencia = []) => {
  if (clientes.length === 0 || usuarios.length === 0) return {};

  const headers = await getAuthHeaders();
  const baseUrl = `${supabaseUrl}/rest/v1`;
  const contactMap = buildContactMap(clientes, usuarios, contactosAgencia);

  // Mapa email → userId (para convertir resultados de paso A)
  const emailByUserId = {};
  usuarios.forEach((u) => {
    if (u.email) emailByUserId[u.id] = u.email.toLowerCase();
  });

  // Mapas inversos: email → clienteIds
  const allUserIds = [...new Set(clientes.flatMap((c) => contactMap[c.id]?.userIds || []))];
  const allEmails = [...new Set(clientes.flatMap((c) => contactMap[c.id]?.emails || []))];

  const emailToClienteIds = {};
  clientes.forEach((c) => {
    (contactMap[c.id]?.emails || []).forEach((email) => {
      if (!emailToClienteIds[email]) emailToClienteIds[email] = [];
      emailToClienteIds[email].push(c.id);
    });
  });

  // Paso A + B en paralelo: buscar actividad_ids via userId y email
  let fetchByUserId = Promise.resolve([]);
  if (allUserIds.length > 0) {
    fetchByUserId = fetchInIdBatches(
      `${baseUrl}/crm_actividades_destinatarios`,
      "usuario_id",
      allUserIds,
      "&select=actividad_id,usuario_id",
      headers
    );
  }

  const fetchByEmail = async () => {
    if (allEmails.length === 0) return [];
    const results = [];
    for (let i = 0; i < allEmails.length; i += EMAIL_BATCH_SIZE) {
      const batch = allEmails.slice(i, i + EMAIL_BATCH_SIZE);
      const emailFilters = batch.map((e) => `email.eq.${e}`).join(",");
      const url = `${baseUrl}/crm_actividades_destinatarios?or=(${emailFilters})&select=actividad_id,email`;
      const data = await fetchPaginated(url, headers);
      results.push(...data);
    }
    return results;
  };

  const [destByUser, destByEmail] = await Promise.all([fetchByUserId, fetchByEmail()]);

  // Construir mapa: actividad_id → Set<email>
  const actividadToEmails = {};
  destByUser.forEach((d) => {
    const email = emailByUserId[d.usuario_id];
    if (!email) return;
    if (!actividadToEmails[d.actividad_id]) actividadToEmails[d.actividad_id] = new Set();
    actividadToEmails[d.actividad_id].add(email);
  });
  destByEmail.forEach((d) => {
    const email = (d.email || "").toLowerCase();
    if (!email) return;
    if (!actividadToEmails[d.actividad_id]) actividadToEmails[d.actividad_id] = new Set();
    actividadToEmails[d.actividad_id].add(email);
  });

  // Paso C: fetch actividades (solo id + fecha), filtrado server-side
  const actIds = Object.keys(actividadToEmails).map(Number);
  if (actIds.length === 0) return {};

  const fechaLimite = new Date();
  fechaLimite.setDate(fechaLimite.getDate() - 90);
  const fechaISO = fechaLimite.toISOString();

  const actividadesRaw = await fetchInIdBatches(
    `${baseUrl}/crm_actividades`,
    "id",
    actIds,
    `&pais=eq.${paisCode}&tipo=in.(MAIL,MEET,CALL)&fecha=gte.${fechaISO}&metadata=not.cs.%7B%22masivo%22%3Atrue%7D&select=id,fecha,cliente_id`,
    headers
  );

  // Distribuir: para cada actividad → emails → clientes → actualizar lastDate
  // Si la actividad tiene cliente_id atribuido → solo cuenta para ese cliente (evita contaminación cruzada)
  // Si no tiene cliente_id → email matching normal para todos los clientes que tengan ese contacto
  const resultado = {};
  actividadesRaw.forEach((act) => {
    const emails = actividadToEmails[act.id];
    if (!emails) return;

    if (act.cliente_id) {
      // Atribuida: solo contar para el cliente asignado
      emails.forEach((email) => {
        if ((emailToClienteIds[email] || []).includes(act.cliente_id)) {
          if (!resultado[act.cliente_id]) resultado[act.cliente_id] = {};
          if (!resultado[act.cliente_id][email] || act.fecha > resultado[act.cliente_id][email]) {
            resultado[act.cliente_id][email] = act.fecha;
          }
        }
      });
    } else {
      // Sin atribuir: solo contar si el contacto pertenece a exactamente 1 cliente.
      // Si el contacto maneja múltiples clientes, no pintar nada (evita falsos verdes cruzados).
      emails.forEach((email) => {
        const clienteIds = emailToClienteIds[email] || [];
        if (clienteIds.length === 1) {
          const cId = clienteIds[0];
          if (!resultado[cId]) resultado[cId] = {};
          if (!resultado[cId][email] || act.fecha > resultado[cId][email]) {
            resultado[cId][email] = act.fecha;
          }
        }
        // Si tiene múltiples clientes → no atribuir hasta que el motor de atribución lo resuelva
      });
    }
  });

  return resultado;
};
